import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useId, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'

import { FinancialCockpitPendingActionOwner } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { usePendingActionsGroups } from '../../../cockpitPendingActions/hooks/usePendingActionsGroups'
import type { IPendingActionWithOffering } from '../../../cockpitPendingActions/types'
import { useAttentionRemindersHiddenByItems } from '../../hooks/useAttentionRemindersHiddenByItems'
import { AttentionStatusActionRows } from '../attentionStatusActionRow'
import { AttentionStatusActions } from '../attentionStatusActions'
import {
  ATTENTION_OWNER_ORDER,
  AttentionStatusOwnerTabs,
  getAttentionOwnerPanelId,
  getAttentionOwnerTabId,
} from '../attentionStatusOwnerTabs'

import styles from './AttentionStatusPendingActions.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.pendingActions

/**
 * One call of `usePendingActionsGroups` cut down to the owner it was asked for.
 *
 * The hook answers for a single owner — it serves the tile's owner tab strip, and a tab it cannot
 * find in the payload falls back to the first owner there is — so a call asked for the PBM can come
 * back holding the reader's own actions. Filtering here is what keeps a group to its owner.
 */
const scopeActions = (
  actions: IPendingActionWithOffering[],
  owner: FinancialCockpitPendingActionOwner
) => actions.filter((action) => action.ActionOwner === owner)

interface IOwnerOfferingGroup {
  offering: ProgramTypes
  actions: IPendingActionWithOffering[]
}

interface IOwnerGroup {
  owner: FinancialCockpitPendingActionOwner
  offerings: IOwnerOfferingGroup[]
  count: number
}

/**
 * An owner's actions split by the plan they act against, in the order the payload lists them.
 *
 * The split is not what the reader sees — the rows of one owner read as one list — but what the
 * action layer needs: its dialogs are scoped to an offering, and a mixed list would open them
 * against the wrong plan.
 */
const groupByOffering = (actions: IPendingActionWithOffering[]): IOwnerOfferingGroup[] =>
  actions.reduce<IOwnerOfferingGroup[]>((groups, action) => {
    const group = groups.find(({ offering }) => offering === action.Offering)

    if (group) {
      group.actions.push(action)
    } else {
      groups.push({ offering: action.Offering, actions: [action] })
    }

    return groups
  }, [])

/**
 * The detail of the `My pending actions` status: the reader's open actions grouped by who has to
 * act, with each action's own call on its row.
 *
 * The sheet groups the list `by PBM/Manager -> category`, and the card row carries its category
 * itself: a sub-header per category would state the same word twice, once above the row and once
 * on it, so the grouping stops at the owner and the rows say the rest.
 *
 * The owners are tabs rather than blocks stacked one under the other. Every owner holds the same
 * kind of row, the reader answers one side at a time, and stacked blocks put the second owner's
 * rows below a list that is already long enough to scroll.
 *
 * The bar keeps no call into the pending actions: the list itself is the way in. The reminder it
 * does keep is drawn in the card's corner, where every act of every attention card is drawn.
 *
 * The rows are the attention-status card row, drawn on the `cockpitPendingActions` action layer
 * rather than on its tile: an action's call, its dialogs and its blocked state live with the
 * action, while the grouping, the headers and the spacing are this card's. Dropping the whole tile
 * in here would put a second tile inside the card, with its own tabs, counters and expand control.
 *
 * `usePendingActionsGroups` is asked once per owner: it narrows its result to one owner tab, so a
 * single call would hand this card one group and silently drop the other two.
 */
export const AttentionStatusPendingActions = () => {
  const tabsId = useId()
  const [pickedOwner, setPickedOwner] = useState<FinancialCockpitPendingActionOwner | null>(null)

  const { actions: mineInScope } = usePendingActionsGroups(
    FinancialCockpitPendingActionOwner.Mine,
    null,
    false
  )
  const { actions: pbmInScope } = usePendingActionsGroups(
    FinancialCockpitPendingActionOwner.PBM,
    null,
    false
  )
  const { actions: managerInScope } = usePendingActionsGroups(
    FinancialCockpitPendingActionOwner.SalesManager,
    null,
    false
  )

  const mineActions = useMemo(
    () => scopeActions(mineInScope, FinancialCockpitPendingActionOwner.Mine),
    [mineInScope]
  )
  const pbmActions = useMemo(
    () => scopeActions(pbmInScope, FinancialCockpitPendingActionOwner.PBM),
    [pbmInScope]
  )
  const managerActions = useMemo(
    () => scopeActions(managerInScope, FinancialCockpitPendingActionOwner.SalesManager),
    [managerInScope]
  )

  const remindersHiddenByItems = useAttentionRemindersHiddenByItems(
    ATTENTION_STATUS_BITS.pendingActions,
    {
      mine: mineActions.length,
      pbm: pbmActions.length,
      manager: managerActions.length,
    }
  )

  const ownerGroups = useMemo<IOwnerGroup[]>(
    () =>
      [mineActions, pbmActions, managerActions]
        .map((ownerActions, index) => ({
          owner: ATTENTION_OWNER_ORDER[index],
          offerings: groupByOffering(ownerActions),
          count: ownerActions.length,
        }))
        .filter(({ count }) => count > 0),
    [mineActions, pbmActions, managerActions]
  )

  /* The tab the reader picked, as long as the payload still carries it: a refresh that clears an
     owner's last action would otherwise leave the card on an empty tab. */
  const activeOwner =
    ownerGroups.find(({ owner }) => owner === pickedOwner)?.owner ?? ownerGroups[0]?.owner ?? null
  const activeGroup = ownerGroups.find(({ owner }) => owner === activeOwner)

  return (
    <div className={styles.root}>
      {ownerGroups.length === 0 ? (
        <p className={styles.empty}>
          <Translation span text={KEYS.allClearSubtitle} />
        </p>
      ) : (
        <div className={styles.groups}>
          <AttentionStatusOwnerTabs
            tabs={ownerGroups}
            activeOwner={activeOwner}
            onSelect={setPickedOwner}
            idPrefix={tabsId}
          />

          {activeGroup && (
            <div
              key={activeGroup.owner}
              role="tabpanel"
              id={getAttentionOwnerPanelId(tabsId, activeGroup.owner)}
              aria-labelledby={getAttentionOwnerTabId(tabsId, activeGroup.owner)}
              className={styles.rows}
            >
              {/* One call per offering: the action layer scopes its dialogs to the offering the
                action belongs to, and a mixed list would open them against the wrong plan. The
                blocks after the first take the divider that makes them read as one list. */}
              {activeGroup.offerings.map(({ offering, actions: offeringActions }, index) => (
                <AttentionStatusActionRows
                  key={offering}
                  offering={offering}
                  actions={offeringActions}
                  showLeadingDivider={index > 0}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* The list itself is the way into the pending actions, so the call drops out; the reminder
        stays, in the card's corner with every other act the reader has. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.pendingActions}
        className={styles.actions}
        hidden={['callPendingAction', ...remindersHiddenByItems]}
      />
    </div>
  )
}

AttentionStatusPendingActions.displayName = 'AttentionStatusPendingActions'
